import React from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    { id: 'information-collection', title: '1. Information We Collect' },
    { id: 'how-we-use', title: '2. How We Use Your Information' },
    { id: 'information-sharing', title: '3. Information Sharing' },
    { id: 'data-security', title: '4. Data Security' },
    { id: 'data-retention', title: '5. Data Retention' },
    { id: 'your-rights', title: '6. Your Rights' },
    { id: 'cookies', title: '7. Cookies and Tracking' },
    { id: 'third-party', title: '8. Third-Party Services' },
    { id: 'children', title: '9. Children\'s Privacy' },
    { id: 'international', title: '10. International Transfers' },
    { id: 'changes', title: '11. Changes to This Policy' },
    { id: 'contact', title: '12. Contact Us' },
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="pt-20 pb-8 sm:pt-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 leading-tight text-slate-900">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-600">
            Last updated: January 31, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Table of Contents */}
            <div className="p-6 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Table of Contents</h2>
              <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-blue-600 hover:text-blue-800 text-sm hover:underline"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>

            {/* Privacy Content */}
            <div className="p-6 sm:p-8 prose prose-slate max-w-none">
              <p className="text-slate-600 leading-relaxed mb-8">
                At SystemDox, we take your privacy seriously. This Privacy Policy explains how we collect,
                use, disclose, and safeguard your information when you use our platform and services.
                Please read this policy carefully. By using our Service, you agree to the collection and
                use of information in accordance with this policy.
              </p>

              <section id="information-collection" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We collect information that you provide directly to us, as well as information
                  collected automatically when you use our Service.
                </p>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">Information You Provide</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Account information (name, email address, password)</li>
                  <li>Profile information (organization name, role, preferences)</li>
                  <li>Content you upload or create (audio recordings, documents, architecture diagrams)</li>
                  <li>Communications with us (support requests, feedback)</li>
                  <li>Payment information (processed by our payment providers)</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">Information Collected Automatically</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Device information (browser type, operating system, device identifiers)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Log data (IP address, access times, referring URLs)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section id="how-we-use" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Process your audio recordings and generate documentation</li>
                  <li>Manage your account and provide customer support</li>
                  <li>Send you technical notices, updates, and administrative messages</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Monitor and analyze trends, usage, and activities</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Develop new products, services, features, and functionality</li>
                </ul>
              </section>

              <section id="information-sharing" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">3. Information Sharing</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We do not sell your personal information. We may share your information in the
                  following circumstances:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Service Providers:</strong> Third parties who perform services on our behalf
                    (cloud hosting, payment processing, analytics)</li>
                  <li><strong>AI Processing:</strong> Audio and text data is processed by AI services to
                    generate documentation (processed in accordance with our data processing agreements)</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to respond to legal process</li>
                  <li><strong>Protection of Rights:</strong> To protect the rights, property, and safety of
                    SystemDox, our users, or others</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale
                    of assets</li>
                  <li><strong>With Your Consent:</strong> When you direct us to share your information</li>
                </ul>
              </section>

              <section id="data-security" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">4. Data Security</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We implement appropriate technical and organizational measures to protect your
                  information, including:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and penetration testing</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Employee training on data protection practices</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  However, no method of transmission over the Internet or electronic storage is 100%
                  secure. While we strive to use commercially acceptable means to protect your information,
                  we cannot guarantee its absolute security.
                </p>
              </section>

              <section id="data-retention" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">5. Data Retention</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We retain your information for as long as your account is active or as needed to
                  provide you services. We will retain and use your information as necessary to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Comply with our legal obligations</li>
                  <li>Resolve disputes and enforce our agreements</li>
                  <li>Maintain business records for a reasonable period</li>
                </ul>
              </section>

              <section id="your-rights" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">6. Your Rights</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal
                  information:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Access:</strong> Request a copy of your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                  <li><strong>Objection:</strong> Object to certain processing of your information</li>
                  <li><strong>Restriction:</strong> Request restriction of processing</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  To exercise these rights, please contact us at privacy@systemdox.com. We will respond
                  to your request within 30 days.
                </p>
              </section>

              <section id="cookies" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">7. Cookies and Tracking</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to collect and track information
                  about your use of our Service. Cookies are small data files stored on your device.
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Essential Cookies:</strong> Required for the Service to function properly</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how you use our Service</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  You can control cookies through your browser settings. Note that disabling cookies
                  may affect the functionality of our Service.
                </p>
              </section>

              <section id="third-party" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">8. Third-Party Services</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Our Service may contain links to third-party websites or services. We are not
                  responsible for the privacy practices of these third parties. We encourage you
                  to review their privacy policies before providing any information.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We use third-party services for various functions, including:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Cloud infrastructure (AWS)</li>
                  <li>Payment processing</li>
                  <li>Analytics</li>
                  <li>AI/ML processing for transcription and documentation generation</li>
                </ul>
              </section>

              <section id="children" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">9. Children's Privacy</h2>
                <p className="text-slate-600 leading-relaxed">
                  Our Service is not intended for individuals under the age of 18. We do not knowingly
                  collect personal information from children under 18. If we become aware that we have
                  collected personal information from a child under 18, we will take steps to delete
                  such information.
                </p>
              </section>

              <section id="international" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">10. International Transfers</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Your information may be transferred to and processed in countries other than your
                  country of residence. These countries may have different data protection laws than
                  your country.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  When we transfer personal information internationally, we implement appropriate
                  safeguards in accordance with applicable law, including standard contractual clauses
                  approved by relevant authorities.
                </p>
              </section>

              <section id="changes" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">11. Changes to This Policy</h2>
                <p className="text-slate-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any
                  material changes by posting the new Privacy Policy on this page and updating the
                  "Last updated" date. You are advised to review this Privacy Policy periodically
                  for any changes.
                </p>
              </section>

              <section id="contact" className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <p className="text-slate-700 font-medium">SystemDox Privacy Team</p>
                  <p className="text-slate-600">Email: privacy@systemdox.com</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default PrivacyPolicy;
